// src/components/DesignationManager.js
import React, { useState, useEffect } from "react";
import { Button, Table, Modal, Form } from "react-bootstrap";
import { adminPost, adminGet, adminDelete, adminPut } from "../../../Services";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";

const DesignationManager = () => {
  const [designations, setDesignations] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    scope: "National",
    maxCount: "",
  });

  // Fetch all designations
  const fetchDesignations = async () => {
    setLoading(true);
    adminGet("/api/designations/getAll")
      .then((res) => {
        setDesignations(res.data);
        console.log(res.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDesignations();
  }, []);

  // Handle Create Designation
  const handleCreate = async (e) => {
    setLoading(true);
    e.preventDefault();

    adminPost("/api/designations", formData)
      .then((res) => {
        toast.success("Designation created successfully!");
        setShowCreateModal(false);
        fetchDesignations();
      })
      .catch((err) => {
        toast.error("Failed to create designation.");
      })
      .finally(() => setLoading(false));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    adminPut(`/api/designations/${editId}`, formData)
      .then(() => {
        toast.success("Designation updated successfully!");
        setShowEditModal(false);
        setEditId(null);
        fetchDesignations();
      })
      .catch(() => {
        toast.error("Failed to update designation.");
      })
      .finally(() => setLoading(false));
  };

  // Handle Delete Designation
  const handleDelete = async () => {
    setLoading(true);
    adminDelete(`/api/designations/${deleteId}`)
      .then((res) => {
        toast.success("Designation deleted successfully!");
        setShowDeleteModal(false);
        fetchDesignations();
      })
      .catch((err) => {
        toast.error("Failed to delete designation.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="">
      <div className="d-flex align-items-center justify-content-between">
        <h3 className="fw-medium" style={{ color: "#1a5276" }}>
          Designation Manager
        </h3>
        <div>
          <Button
            variant="primary"
            size="sm"
            className="flex-shrink-0"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Designation
          </Button>
        </div>
      </div>

      {/* Table List */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
          <Table striped bordered hover className="mt-3 rounded">
            <thead className="table-primary sticky-top">
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Scope</th>
                <th>Max Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {designations.length > 0 ? (
                designations.map((d, index) => (
                  <tr key={d.id}>
                    <td>{index + 1}</td>
                    <td>{d.name}</td>
                    <td>{d.scope}</td>
                    <td>{d.maxCount ?? "Unlimited"}</td>
                    <td className="d-flex gap-2">
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setEditId(d.id);
                          setFormData({
                            name: d.name,
                            scope: d.scope,
                            maxCount: d.maxCount ?? "",
                          });
                          setShowEditModal(true);
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setDeleteId(d.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No designations found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Designation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreate}>
            <Form.Group className="mb-3">
              <Form.Label>Designation Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Scope</Form.Label>
              <Form.Select
                value={formData.scope}
                onChange={(e) =>
                  setFormData({ ...formData, scope: e.target.value })
                }
              >
                <option value="National">National</option>
                <option value="State">State</option>
                <option value="District">District</option>
                <option value="Block">Block</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Max Count (optional)</Form.Label>
              <Form.Control
                type="number"
                placeholder="Leave blank for unlimited"
                value={formData.maxCount}
                onChange={(e) =>
                  setFormData({ ...formData, maxCount: e.target.value })
                }
                min="1"
              />
            </Form.Group>

            <Button type="submit" variant="success">
              Create
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Designation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdate}>
            <Form.Group className="mb-3">
              <Form.Label>Designation Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Scope</Form.Label>
              <Form.Select
                value={formData.scope}
                onChange={(e) =>
                  setFormData({ ...formData, scope: e.target.value })
                }
              >
                <option value="National">National</option>
                <option value="State">State</option>
                <option value="District">District</option>
                <option value="Block">Block</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Max Count (optional)</Form.Label>
              <Form.Control
                type="number"
                placeholder="Leave blank for unlimited"
                value={formData.maxCount}
                onChange={(e) =>
                  setFormData({ ...formData, maxCount: e.target.value })
                }
                min="1"
              />
            </Form.Group>

            <Button type="submit" variant="success">
              Update
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Designation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this designation?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DesignationManager;
